import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { BalanceProvider } from "./contexts/BalanceContext";
import Layout from "./components/Layout";
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/auth/RegisterPage";
import CompanySignupPage from "./pages/auth/CompanySignupPage";
import CandidateSignupPage from "./pages/auth/CandidateSignupPage";
import CompanySetupPage from "./pages/auth/CompanySetupPage";
import EmailVerificationPage from "./pages/auth/EmailVerificationPage";
import PasswordSetupPage from "./pages/auth/PasswordSetupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CandidateDashboard from "./pages/candidate/CandidateDashboard";
import CandidateOnboarding from "./pages/candidate/CandidateOnboarding";
import CandidateProfileEditNew from "./pages/candidate/CandidateProfileEditNew";
import CandidateProfileView from "./pages/candidate/CandidateProfileView";
import CandidateAccountSettings from "./pages/candidate/CandidateAccountSettings";
import CandidateAnalytics from "./pages/candidate/CandidateAnalytics";
import CandidateNotifications from "./pages/candidate/CandidateNotifications";
import CandidateMessages from "./pages/candidate/CandidateMessages";
import CandidateConversationDetail from "./pages/candidate/CandidateConversationDetail";

import JobBrowsing from "./pages/candidate/JobBrowsing";
import JobApplications from "./pages/candidate/jobBrowsing/view/JobBrowsing";
import CandidateJobManagement from "./pages/candidate/CandidateJobManagement";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import CandidateSearch from "./pages/company/CandidateSearch";
import CandidateProfile from "./pages/company/CandidateProfile";
import ResumeUpload from "./pages/company/ResumeUpload";
import JobManagement from "./pages/company/JobManagement/views/JobManagement";
import UserManagement from "./pages/company/Team/UserManagement";
import CompanyMessages from "./pages/company/CompanyMessages";
import ConversationDetail from "./pages/company/ConversationDetail";
import SuperAdminDashboard from "./pages/super-admin/SuperAdminDashboard";
import CompanyManagement from "./pages/super-admin/CompanyManagement";
import SystemAnalytics from "./pages/super-admin/SystemAnalytics";
import SuperAdminUserManagement from "./pages/super-admin/UserManagement";
import PublicProfilePage from "./pages/PublicProfilePage";
import NotFoundPage from "./pages/NotFoundPage";
import Finance from "./pages/super-admin/Finance";
import JobSeekers from "./pages/super-admin/JobSeekers";
import Subscriptions from "./pages/super-admin/Subscriptions";
import Settings from "./pages/super-admin/Settings";

import LegalPage from "./pages/privacyAndTerms/view/termsPolicyPage";
import AboutPage from "./pages/about/view/AboutPage";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import JobDetails from "./pages/company/JobManagement/views/JobDetails";
import ApplicationDetails from "./pages/company/JobManagement/views/ApplicationDetailsPage";
import { ApprovalFlowView } from "./pages/company/CompanySettings/JobApproval/view/ApprovalFlowList";
import { ManageApprovalFlowView } from "./pages/company/CompanySettings/JobApproval/view/ManageApprovalFlow";
import { PendingApprovalsPage } from "./pages/company/JobManagement/views/PendingApprovalsPage";
import { PendingJobs } from "./pages/super-admin/Jobs";
import JobDetailsPage from "./pages/candidate/jobDetailsPage/view/JobDetailsPage";
import JobApplicationDetailsPage from "./pages/candidate/jobApplicationsDetailsPage/view/JobApplicationDetailsPage";
import { CompanySettingTabsView } from "./pages/company/CompanySettings/CompanySettingTabsView";
import ApplyJobPage from "./pages/candidate/jobDetailsPage/view/ApplyJobView";
import LoginPage from "./pages/auth/LoginPage";
import SupportTickets from "./pages/super-admin/SupportTickets";
import { SuperAdminPendingApprovalJobDetails } from "./pages/super-admin/view/superAdminPendingApprovalDetails";
import CandidateProfileBoost from "./pages/candidate/CandidateProfileBooat/view/CandidateProfileBoost";
import { CompanyNotification } from "./pages/company/Notifications/view/CompanyNotification";

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Something went wrong
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                An unexpected error occurred. Please refresh the page or try
                again later.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Refresh Page
                </button>
              </div>
              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="text-sm text-gray-600 cursor-pointer">
                    Error Details
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrandingProvider>
          <AuthProvider>
            <BalanceProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <div className="App">
                  <Routes>
                    <Route
                      path="/jobs/browse"
                      element={
                        <Layout>
                          <JobBrowsing />
                        </Layout>
                      }
                    />{" "}
                    {/* Home / job browsing page */}
                    <Route
                      path="/jobs/browse/:jobId"
                      element={
                        <Layout>
                          <JobDetailsPage />
                        </Layout>
                      }
                    />{" "}
                    {/* Job details page */}
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<LandingPage />} />
                    <Route path="/terms" element={<LegalPage type="terms" />} />
                    <Route
                      path="/privacy"
                      element={<LegalPage type="privacy" />}
                    />
                    <Route path="/about" element={<AboutPage />} />
                    <Route
                      path="/billing/success"
                      element={<BillingSuccess />}
                    />
                    <Route path="/billing/cancel" element={<BillingCancel />} />
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/register" element={<RegisterPage />} />
                    <Route
                      path="/auth/company-signup"
                      element={<CompanySignupPage />}
                    />
                    <Route
                      path="/auth/candidate-signup"
                      element={<CandidateSignupPage />}
                    />
                    <Route
                      path="/auth/company-setup"
                      element={<CompanySetupPage />}
                    />
                    <Route
                      path="/auth/email-verification"
                      element={<EmailVerificationPage />}
                    />
                    <Route
                      path="/auth/verify-email"
                      element={<EmailVerificationPage />}
                    />
                    <Route
                      path="/auth/password-setup"
                      element={<PasswordSetupPage />}
                    />
                    <Route
                      path="/auth/forgot-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path="/auth/reset-password"
                      element={<ResetPasswordPage />}
                    />
                    {/* Public Profile Route (no auth required) */}
                    <Route
                      path="/profile/:slug"
                      element={<PublicProfilePage />}
                    />
                    {/* Protected routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      }
                    />
                    {/* Candidate routes */}
                    <Route
                      path="/candidate/onboarding"
                      element={<CandidateOnboarding />}
                    />
                    <Route
                      path="/candidate/dashboard"
                      element={
                        <Layout>
                          <CandidateDashboard />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/profile"
                      element={
                        <Layout>
                          <CandidateProfileView />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/profile/edit"
                      element={
                        <Layout>
                          <CandidateProfileEditNew />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/account-settings"
                      element={
                        <Layout>
                          <CandidateAccountSettings />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/change-password"
                      element={
                        <Layout>
                          <PasswordSetupPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/analytics"
                      element={
                        <Layout>
                          <CandidateAnalytics />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/notifications"
                      element={
                        <Layout>
                          <CandidateNotifications />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/profile-boost"
                      element={
                        <Layout>
                          <CandidateProfileBoost />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/job-browsing"
                      element={
                        <Layout>
                          <JobBrowsing />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/jobs"
                      element={
                        <Layout>
                          <CandidateJobManagement />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/job-applications"
                      element={
                        <Layout>
                          <JobApplications />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/applications"
                      element={
                        <Layout>
                          <JobApplications />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/applications/:id"
                      element={
                        <Layout>
                          <JobApplicationDetailsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/jobs/browse/:jobId/apply"
                      element={
                        <Layout>
                          <ApplyJobPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/messages"
                      element={
                        <Layout>
                          <CandidateMessages />
                        </Layout>
                      }
                    />
                    <Route
                      path="/candidate/messages/:conversationId"
                      element={
                        <Layout>
                          <CandidateConversationDetail />
                        </Layout>
                      }
                    />
                    {/* Company routes */}
                    <Route
                      path="/company/dashboard"
                      element={
                        <Layout>
                          <CompanyDashboard />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/candidates"
                      element={
                        <Layout>
                          <CandidateSearch />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/search"
                      element={
                        <Layout>
                          <CandidateSearch />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/candidate/:id"
                      element={
                        <Layout>
                          <CandidateProfile />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/upload"
                      element={
                        <Layout>
                          <ResumeUpload />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/jobs"
                      element={
                        <Layout>
                          <JobManagement />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/JobDetails/:id"
                      element={
                        <Layout>
                          <JobDetails />
                        </Layout>
                      }
                    />
                    {/* <Route
                      path="/company/applications"
                      element={
                        <Layout>
                          <ApplicationManagement />
                        </Layout>
                      }
                    /> */}
                    <Route
                      path="/company/pendingApproval"
                      element={
                        <Layout>
                          <PendingApprovalsPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/users"
                      element={
                        <Layout>
                          <UserManagement />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/flowsetting"
                      element={
                        <Layout>
                          <CompanySettingTabsView />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/messages"
                      element={
                        <Layout>
                          {" "}
                          <CompanyMessages />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/notifications"
                      element={
                        <Layout>
                          {" "}
                          <CompanyNotification />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/messages/:conversationId"
                      element={
                        <Layout>
                          <ConversationDetail />
                        </Layout>
                      }
                    />
                    <Route
                      path="/company/change-password"
                      element={
                        <Layout>
                          <PasswordSetupPage />
                        </Layout>
                      }
                    />
                    {/* Super Admin routes */}
                    <Route
                      path="/super-admin/dashboard"
                      element={
                        <Layout>
                          <SuperAdminDashboard />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/companies"
                      element={
                        <Layout>
                          <CompanyManagement />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/users"
                      element={
                        <Layout>
                          <SuperAdminUserManagement />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/finance"
                      element={
                        <Layout>
                          <Finance />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/job-seekers"
                      element={
                        <Layout>
                          <JobSeekers />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/jobS"
                      element={
                        <Layout>
                          <PendingJobs />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/PendingJobDetails/:id"
                      element={
                        <Layout>
                          <SuperAdminPendingApprovalJobDetails />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/subscriptions"
                      element={
                        <Layout>
                          <Subscriptions />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/settings"
                      element={
                        <Layout>
                          <Settings />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/support-tickets"
                      element={
                        <Layout>
                          <SupportTickets />
                        </Layout>
                      }
                    />
                    <Route
                      path="/super-admin/analytics"
                      element={
                        <Layout>
                          <SystemAnalytics />
                        </Layout>
                      }
                    />
                    {/* Catch all route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </div>
              </Router>
            </BalanceProvider>
          </AuthProvider>
        </BrandingProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
